import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createPageUrl } from "@/utils";
import { Trophy, Heart, Play, Pause, Vote, Search, Music, MessageSquare } from "lucide-react";
import SendMessageButton from "@/components/SendMessageButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "sonner";
import { usePlayer } from "@/contexts/PlayerContext";
import { hapticLight, hapticMedium, hapticSuccess, hapticError } from "@/utils/haptic";

import { WinnerBadge } from "@/components/WinnerBadge";
import { LeaderboardBadge } from "@/components/LeaderboardBadge";

import { getDefaultCoverImage } from "@/utils/defaultCoverImage";
import { LeaderboardSkeleton } from "@/components/ui/skeleton-card";

// Debug logging helper - only logs in development, redacts PII
const debugLog = (...args) => {
  if (import.meta.env.DEV) {
    // Redact PII from logged objects
    const redactPII = (obj) => {
      if (!obj || typeof obj !== 'object') return obj;
      if (Array.isArray(obj)) return obj.map(redactPII);
      
      const redacted = { ...obj };
      // Redact common PII fields
      if (redacted.id) redacted.id = '[REDACTED]';
      if (redacted.user_id) redacted.user_id = '[REDACTED]';
      if (redacted.voter_id) redacted.voter_id = '[REDACTED]';
      if (redacted.track_id) redacted.track_id = '[REDACTED]';
      if (redacted.selectedTrackId) redacted.selectedTrackId = '[REDACTED]';
      if (redacted.currentUser) redacted.currentUser = '[REDACTED]';
      if (redacted.artist_id) redacted.artist_id = '[REDACTED]';
      // Recursively redact nested objects
      Object.keys(redacted).forEach(key => {
        if (typeof redacted[key] === 'object' && redacted[key] !== null) {
          redacted[key] = redactPII(redacted[key]);
        }
      });
      return redacted;
    };
    
    const redactedArgs = args.map(arg => {
      if (typeof arg === 'object' && arg !== null) {
        return redactPII(arg);
      }
      // For string messages, check if they contain IDs and redact
      if (typeof arg === 'string') {
        return arg.replace(/\b(id|user_id|track_id|voter_id|artist_id):\s*[^\s,}]+/gi, '$1: [REDACTED]');
      }
      return arg;
    });
    
    console.debug(...redactedArgs);
  }
};


export default function Leaderboard() {
  const navigate = useNavigate();
  const { playTrack, currentTrack, isPlaying, togglePlayPause } = usePlayer();
  const queryClient = useQueryClient();
  const [currentUser, setCurrentUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [voteDialogOpen, setVoteDialogOpen] = useState(false);
  const [selectedTrackId, setSelectedTrackId] = useState(null);
  const [voteAmount, setVoteAmount] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const tracksPerPage = 50;
  const [voteBoostMode, setVoteBoostMode] = useState(false);
  const lastClickTimeRef = useRef(0);
  const lastClickTrackIdRef = useRef(null);

  useEffect(() => {
    document.title = "Leaderboard - Soundope";
    loadUser();

    // Dev flags are NOT persisted in production - they reset on page reload
    // In development only, allow localStorage persistence for convenience
    if (import.meta.env.DEV) {
      const checkBoostMode = () => {
        // Vote boost should only be active when dev menu is unlocked AND boost mode is enabled
        const devUnlocked = localStorage.getItem('dev_unlocked') === 'true';
        const boostEnabled = localStorage.getItem('vote_boost_mode') === 'true';
        // Only show boost label if both conditions are met
        setVoteBoostMode(devUnlocked && boostEnabled);
      };
      checkBoostMode();
      
      // Listen for storage changes (dev only)
      window.addEventListener('storage', checkBoostMode);
      const interval = setInterval(checkBoostMode, 500); // Check every 500ms
      
      return () => {
        window.removeEventListener('storage', checkBoostMode);
        clearInterval(interval);
      };
    }
    // In production, voteBoostMode defaults to false (set in useState above)
  }, []);

  // Handle double-click for vote boost
  const handleDoubleClickBoost = async (trackId) => {
    if (!voteBoostMode) return;
    
    const now = Date.now();
    const isDoubleClick = lastClickTrackIdRef.current === trackId && (now - lastClickTimeRef.current) < 500; // 500ms double-click window
    
    if (isDoubleClick) {
      // Reset double-click tracking IMMEDIATELY (synchronously) to prevent rapid clicks from triggering multiple boosts
      lastClickTimeRef.current = 0;
      lastClickTrackIdRef.current = null;
      
      // Generate random votes between 10-20
      const randomVotes = Math.floor(Math.random() * 11) + 10; // 10-20 inclusive
      
      hapticMedium();
      
      try {
        // Increment praise count via RPC function (bypasses RLS safely)
        const { data: newPraiseCount, error: rpcError } = await supabase
          .rpc('increment_track_praise_count', { 
            track_id: trackId, 
            increment_by: randomVotes 
          });

        if (rpcError) throw rpcError;

        hapticSuccess();
        toast.success(`Added ${randomVotes} votes to track!`);
        refetch();
      } catch (error) {
        hapticError();
        toast.error("Failed to add votes: " + error.message);
      }
    } else {
      // First click - record it (synchronously)
      lastClickTimeRef.current = now;
      lastClickTrackIdRef.current = trackId;
    }
  };

  const loadUser = async () => {
    try {
      const { data: sessionResult } = await supabase.auth.getSession();
      const session = sessionResult?.session;
      if (session?.user) {
        const { data: profile } = await supabase
          .from("profiles")
          .select("*")
          .eq("id", session.user.id)
          .single();
        setCurrentUser(profile);
      }
    } catch (error) {
      debugLog("Error loading user:", error);
    }
  };

  const { data: leaderboardTracks = [], isLoading, refetch } = useQuery({
    queryKey: ["leaderboard-tracks"],
    queryFn: async () => {
      debugLog("[Leaderboard] Fetching tracks from database...");
      // Check if leaderboard restore is enabled (bypasses monthly filter)
      const leaderboardRestored = localStorage.getItem('leaderboard_restored') === 'true';
      
      const { data: tracks, error } = await supabase
        .from("tracks")
        .select("*")
        .eq("is_active", true)
        .order("praise_count", { ascending: false })
        .order("created_at", { ascending: false })
        .limit(500);

      if (error) {
        debugLog("[Leaderboard] Error fetching tracks", error);
        debugLog("[Leaderboard] Error details", {
          message: error.message,
          code: error.code,
          details: error.details,
          hint: error.hint
        });
        toast.error(`Failed to load leaderboard: ${error.message || "Unknown error"}`);
        throw error;
      }
      
      // Fetch badge status for all artists
      if (tracks && tracks.length > 0) {
        const artistIds = [...new Set(tracks.map(t => t.artist_id))];
        const { data: profiles, error: profilesError } = await supabase
          .from("profiles")
          .select("id, has_winner_badge")
          .in("id", artistIds);
        
        if (profilesError) {
          debugLog("[Leaderboard] Error fetching profiles", profilesError);
          // Don't throw - continue without badge data
        }
        
        const badgeMap = new Map(profiles?.map(p => [p.id, p.has_winner_badge]) || []);
        
        console.log(`[Leaderboard] Total tracks fetched: ${tracks.length}, totalPages will be: ${Math.ceil(tracks.length / 50)}`);
        // Apply client-side sort to ensure correct ordering
        const sortedTracks = [...tracks].sort((a, b) => {
          const voteDiff = (b.praise_count || 0) - (a.praise_count || 0);
          if (voteDiff !== 0) return voteDiff;
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        });
        
        return sortedTracks.map(track => ({
          ...track,
          artist_has_badge: badgeMap.get(track.artist_id) || false
        }));
      }
      
      debugLog(`[Leaderboard] Successfully fetched ${tracks?.length || 0} tracks`);
      return tracks || [];
    },
    retry: 2, // Retry failed requests twice
  });

  useEffect(() => {
    if (leaderboardTracks.length > 0 && !currentTrack) {
      playTrack(leaderboardTracks[0], leaderboardTracks, 0, true);
    }
  }, [leaderboardTracks, playTrack, currentTrack]);

  // Reset to page 1 when search query changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const openVoteDialog = (trackId) => {
    if (!currentUser) {
      hapticError();
      toast.error("Please sign in to vote");
      navigate(createPageUrl("Auth"));
      return;
    }

    hapticLight();
    setSelectedTrackId(trackId);
    setVoteAmount("");
    setVoteDialogOpen(true);
  };

  const handleVote = async () => {
    const votes = parseInt(voteAmount);
    
    debugLog("handleVote called with:", { votes, selectedTrackId, currentUser: currentUser?.id });
    
    if (!votes || votes <= 0) {
      hapticError();
      toast.error("Please enter a valid number of votes");
      return;
    }

    if (!currentUser || currentUser.weekly_votes_remaining === null || currentUser.weekly_votes_remaining === undefined) {
      debugLog("Invalid user state:", { currentUser, weekly_votes_remaining: currentUser?.weekly_votes_remaining });
      hapticError();
      toast.error("Unable to vote - please refresh and try again");
      return;
    }

    if (votes > currentUser.weekly_votes_remaining) {
      hapticError();
      toast.error(`You only have ${currentUser.weekly_votes_remaining} votes remaining`);
      return;
    }

    hapticMedium();

    try {
      // Get current track data
      const track = leaderboardTracks.find(t => t.id === selectedTrackId);
      if (!track) {
        debugLog("Track not found in leaderboardTracks:", selectedTrackId);
        throw new Error("Track not found");
      }

      debugLog("Attempting to insert vote record...", { track_id: selectedTrackId, voter_id: currentUser.id });
      
      // Insert vote record via Supabase
      const { error: voteError } = await supabase
        .from("votes")
        .insert({
          track_id: selectedTrackId,
          voter_id: currentUser.id,
          vote_type: "praise"
        });

      // If already voted (unique constraint), continue anyway
      if (voteError && !voteError.message?.includes('duplicate') && !voteError.message?.includes('unique')) {
        debugLog("Vote insert error:", voteError);
        throw voteError;
      }
      
      debugLog("Vote record handled successfully");

      // Increment praise_count via RPC function (bypasses RLS safely)
      debugLog("Incrementing praise_count via RPC...", { track_id: selectedTrackId, amount: votes });
      
      const { data: newPraiseCount, error: rpcError } = await supabase
        .rpc('increment_track_praise_count', { 
          track_id: selectedTrackId, 
          increment_by: votes 
        });

      if (rpcError) throw rpcError;
      
      debugLog("Successfully incremented praise_count to:", newPraiseCount);

      // Optimistically update the track's praise_count in the query cache immediately
      queryClient.setQueryData(["leaderboard-tracks"], (oldTracks) => {
        if (!oldTracks) return oldTracks;
        return oldTracks.map(track => 
          track.id === selectedTrackId 
            ? { ...track, praise_count: newPraiseCount }
            : track
        );
      });

      // Decrement weekly votes via Supabase
      const newWeeklyVotes = currentUser.weekly_votes_remaining - votes;
      
      const { error: profileError } = await supabase
        .from("profiles")
        .update({ weekly_votes_remaining: newWeeklyVotes })
        .eq("id", currentUser.id);
      
      if (profileError) {
        debugLog("Profile update error:", profileError);
        throw profileError;
      }

      // Update local state
      setCurrentUser({ ...currentUser, weekly_votes_remaining: newWeeklyVotes });

      hapticSuccess();
      toast.success(`${votes} votes cast successfully!`);
      setVoteDialogOpen(false);
      setVoteAmount("");
      
      // Refetch to ensure data is in sync (but UI already updated optimistically)
      refetch();
    } catch (error) {
      debugLog("Vote error:", error);
      hapticError();
      toast.error("Failed to cast vote: " + (error.message || "Unknown error"));
    }
  };

  const handlePlayTrack = (track, globalIndex) => {
    if (currentTrack && currentTrack.id === track.id) {
      togglePlayPause();
      return;
    }

    playTrack(track, filteredTracks, globalIndex, true);
    toast.success(`Now playing: ${track.title}`);
  };


  const getMedalIcon = (rank) => {
    if (rank === 0) return <Trophy className="w-6 h-6 text-yellow-500" />;
    if (rank === 1) return <Trophy className="w-6 h-6 text-gray-400" />;
    if (rank === 2) return <Trophy className="w-6 h-6 text-amber-600" />;
    return <span className="w-6 h-6 flex items-center justify-center font-medium text-[#808080]">#{rank + 1}</span>;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen px-4 py-8 pb-32">
        <div className="max-w-4xl mx-auto">
          <div className="neuro-base rounded-3xl p-8 mb-6">
            <div className="flex items-center gap-4 mb-6">
              <h1 className="text-3xl font-bold text-foreground">Leaderboard</h1>
            </div>
            <LeaderboardSkeleton count={10} />
          </div>
        </div>
      </div>
    );
  }

  const filteredTracks = leaderboardTracks.filter(track => 
    track.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    track.artist_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Pagination logic
  const totalPages = Math.ceil(filteredTracks.length / tracksPerPage);
  const startIndex = (currentPage - 1) * tracksPerPage;
  const endIndex = startIndex + tracksPerPage;
  const paginatedTracks = filteredTracks.slice(startIndex, endIndex);

  return (
    <div className="min-h-screen px-4 py-8 pb-32">
      <div className="max-w-4xl mx-auto">
        <div className="neuro-base rounded-3xl p-8 mb-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <div>
                <h1 className="text-xl md:text-3xl font-light text-[#d0d0d0]">Monthly Leaderboard</h1>
                <p className="text-sm md:text-2xl text-[#808080]">
                  Vote for your favorite tracks this month
                  {currentUser && (
                    <span className="ml-2 text-[#a0a0a0]">
                      • {currentUser.weekly_votes_remaining || 0} votes remaining
                    </span>
                  )}
                </p>
              </div>
            </div>
            <Button
              onClick={() => navigate(createPageUrl("AnnualLeaderboard"))}
              className="neuro-base active:neuro-pressed rounded-xl px-4 py-2 text-sm"
            >
              <span className="text-[#a0a0a0]">Annual Leaderboard</span>
            </Button>
          </div>

          <div className="neuro-pressed rounded-2xl p-4 mb-6">
            <p className="text-xl text-[#808080] text-center">
              Top 10 tracks will carry over to next month with bonus votes (10 votes for 1st place down to 1 vote for 10th place)
            </p>
          </div>

          {/* Tabs for Tracks and Playlists */}
          <Tabs defaultValue="tracks" className="mb-6">
            <TabsList className="w-full neuro-base rounded-2xl p-1 mb-6">
              <TabsTrigger value="tracks" className="flex-1 rounded-xl data-[state=active]:neuro-pressed">
                <Music className="w-4 h-4 mr-2" />
                Tracks
              </TabsTrigger>
              <TabsTrigger value="playlists" className="flex-1 rounded-xl data-[state=active]:neuro-pressed">
                <Trophy className="w-4 h-4 mr-2" />
                Playlists
              </TabsTrigger>
            </TabsList>

            <TabsContent value="tracks">
              {/* Search Bar */}
              <div className="relative mb-6">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#707070]" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search for a track..."
                  className="w-full bg-[#0a0a0a] border-none text-[#d0d0d0] placeholder:text-[#606060] neuro-pressed rounded-2xl pl-10 pr-4 py-3"
                />
              </div>

          {filteredTracks.length === 0 ? (
            <div className="text-center py-12">
              <Trophy className="w-16 h-16 mx-auto text-[#606060] mb-4" />
              <p className="text-[#808080] mb-4">No tracks on the leaderboard yet</p>
              <Button
                onClick={() => navigate(createPageUrl("Upload"))}
                className="neuro-base active:neuro-pressed rounded-2xl px-6 py-3"
                tooltip="Upload your track to compete on the leaderboard"
              >
                <span className="text-[#a0a0a0]">Upload a Track</span>
              </Button>
              </div>
              ) : (
                <>
                <div className="space-y-3">
                  {paginatedTracks.map((track) => {
                    const originalIndex = leaderboardTracks.findIndex(t => t.id === track.id);
                    return (
                      <div
                        key={track.id}
                        className={`neuro-flat rounded-2xl p-3 md:p-5 smooth-transition hover:scale-[1.01] ${
                          originalIndex < 3 ? "border-2 border-[#2a2a2a]" : ""
                        }`}
                      >
                        <div className="flex flex-col md:flex-row items-center gap-3 md:gap-5">
                          <div className="flex-shrink-0 w-12 flex justify-center">
                            {getMedalIcon(originalIndex)}
                          </div>
                    
                    <div className="flex-shrink-0 relative group">
                      <img
                        src={track.cover_image_url || getDefaultCoverImage(track.id)}
                        alt={track.title}
                        className={`w-20 h-20 rounded-xl object-cover ${voteBoostMode ? 'cursor-pointer ring-2 ring-muted-foreground' : ''}`}
                        onClick={(e) => {
                          if (voteBoostMode) {
                            e.preventDefault();
                            e.stopPropagation();
                            handleDoubleClickBoost(track.id);
                          }
                        }}
                      />
                      {!voteBoostMode && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                onClick={() => handlePlayTrack(track, originalIndex)}
                                className="absolute inset-0 bg-black/50 rounded-xl flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                {currentTrack && currentTrack.id === track.id && isPlaying ? (
                                  <Pause className="w-8 h-8 text-gray-400 fill-gray-400" />
                                ) : (
                                  <Play className="w-8 h-8 text-gray-400 fill-gray-400" />
                                )}
                              </button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>{currentTrack && currentTrack.id === track.id && isPlaying ? "Pause track" : "Play track"}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                      {voteBoostMode && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="absolute inset-0 bg-muted/40 rounded-xl flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                <span className="text-xs text-muted-foreground font-bold">BOOST</span>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Double-click to add 10-20 random votes</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </div>
                    
                    <div className="flex-1 min-w-0 text-center md:text-left">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              onClick={() => navigate(createPageUrl("TrackDetails") + `?id=${track.id}`)}
                              className="text-left w-full"
                            >
                              <h3 className="text-lg md:text-2xl font-medium text-[#d0d0d0] mb-1 truncate hover:text-[#a0a0a0] transition-colors break-words">
                                {track.title}
                              </h3>
                              <p className="text-base md:text-lg text-[#808080] mb-2 flex items-center gap-1.5">
                                {track.artist_name}
                                {track.artist_has_badge && <WinnerBadge />}
                                {originalIndex < 10 && <LeaderboardBadge position={originalIndex + 1} />}
                              </p>
                            </button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>View track details and feedback</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                      
                      {track.genres && track.genres.length > 0 && (
                        <div className="flex gap-2 flex-wrap justify-center md:justify-start">
                          {track.genres.slice(0, 2).map((genre, idx) => (
                            <span
                              key={idx}
                              className="text-base px-2 py-1 rounded-lg bg-[#1a1a1a] text-[#707070]"
                            >
                              {genre}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-3 md:gap-5">
                      <div className="text-center">
                        <div className="flex items-center gap-2 mb-1">
                          <Heart className="w-4 h-4 text-[#a08080]" />
                          <span className="text-base md:text-xl font-light text-[#d0d0d0]">{track.praise_count}</span>
                        </div>
                        <p className="text-xs text-[#707070]">Votes</p>
                      </div>
                      
                      {track.artist_id && currentUser && track.artist_id !== currentUser.id && (
                        <SendMessageButton
                          recipientId={track.artist_id}
                          recipientName={track.artist_name}
                          currentUser={currentUser}
                          trackTitle={track.title}
                        />
                      )}
                      
                      <Button
                        onClick={() => openVoteDialog(track.id)}
                        disabled={!currentUser}
                        className="neuro-base active:neuro-pressed rounded-2xl px-4 md:px-6 py-2 md:py-3 flex items-center gap-2 text-sm md:text-base"
                        tooltip={!currentUser ? "Sign in to vote" : "Vote for this track on the leaderboard"}
                      >
                        <Vote className="w-4 h-4 text-[#a0a0a0]" />
                        <span className="text-[#a0a0a0]">Vote</span>
                      </Button>
                    </div>
                      </div>
                    </div>
                    );
                  })}
                </div>
                
                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <div className="flex flex-wrap items-center justify-center gap-2 mt-6">
                    <Button
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className="neuro-base active:neuro-pressed rounded-2xl px-4 py-2 disabled:opacity-50"
                    >
                      <span className="text-[#a0a0a0]">Previous</span>
                    </Button>
                    
                    {/* Page number buttons */}
                    <div className="flex flex-wrap items-center justify-center gap-1">
                      {(() => {
                        const pages = [];
                        const maxVisiblePages = 7;
                        
                        if (totalPages <= maxVisiblePages) {
                          // Show all pages if total is small
                          for (let i = 1; i <= totalPages; i++) {
                            pages.push(i);
                          }
                        } else {
                          // Always show first page
                          pages.push(1);
                          
                          if (currentPage > 3) {
                            pages.push('...');
                          }
                          
                          // Show pages around current
                          const start = Math.max(2, currentPage - 1);
                          const end = Math.min(totalPages - 1, currentPage + 1);
                          
                          for (let i = start; i <= end; i++) {
                            if (!pages.includes(i)) pages.push(i);
                          }
                          
                          if (currentPage < totalPages - 2) {
                            pages.push('...');
                          }
                          
                          // Always show last page
                          if (!pages.includes(totalPages)) {
                            pages.push(totalPages);
                          }
                        }
                        
                        return pages.map((page, index) => {
                          if (page === '...') {
                            return (
                              <span key={`ellipsis-${index}`} className="px-2 text-[#606060]">
                                ...
                              </span>
                            );
                          }
                          return (
                            <Button
                              key={page}
                              onClick={() => setCurrentPage(page)}
                              className={`min-w-[40px] h-10 rounded-xl ${
                                currentPage === page 
                                  ? 'neuro-pressed text-[#d0d0d0]' 
                                  : 'neuro-base active:neuro-pressed text-[#808080] hover:text-[#a0a0a0]'
                              }`}
                            >
                              {page}
                            </Button>
                          );
                        });
                      })()}
                    </div>
                    
                    <Button
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      className="neuro-base active:neuro-pressed rounded-2xl px-4 py-2 disabled:opacity-50"
                    >
                      <span className="text-[#a0a0a0]">Next</span>
                    </Button>
                  </div>
                )}
                </>
              )}
            </TabsContent>

            <TabsContent value="playlists">
              <div className="text-center py-12">
                <Trophy className="w-16 h-16 mx-auto text-[#606060] mb-4" />
                <p className="text-[#808080] mb-2">Playlist voting coming soon!</p>
                <p className="text-xs text-[#606060]">Vote for your favorite curated playlists</p>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Vote Dialog */}
      <Dialog open={voteDialogOpen} onOpenChange={setVoteDialogOpen}>
        <DialogContent className="bg-[#0a0a0a] border-[#2a2a2a] neuro-base rounded-3xl">
          <DialogHeader>
            <DialogTitle className="text-[#d0d0d0]">Cast Your Votes</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-[#808080]">
              You have {currentUser?.weekly_votes_remaining || 0} votes remaining
            </p>
            <Input
              type="number"
              min="1"
              max={currentUser?.weekly_votes_remaining || 0}
              placeholder="How many votes?"
              value={voteAmount}
              onChange={(e) => setVoteAmount(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleVote()}
              className="bg-[#0a0a0a] text-[#d0d0d0] border-[#2a2a2a] neuro-pressed rounded-2xl"
            />
            <Button
              onClick={handleVote}
              className="w-full neuro-base active:neuro-pressed rounded-2xl px-6 py-3"
              tooltip="Submit your votes for this track"
            >
              <span className="text-[#a0a0a0]">Cast Votes</span>
            </Button>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
}
